import {
  cloneElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

export interface TooltipProps {
  /** Tooltip text (kept short — it renders on one line). */
  content: ReactNode;
  /** The trigger; must be a focusable element (button/link/input) — a plain
   * `<span>`/`<td>` never receives keyboard focus, so the tooltip would be
   * mouse-only. `aria-describedby` is injected onto it directly. */
  children: ReactElement;
  /**
   * Set when `content` merely repeats text `children` already renders in full
   * (e.g. revealing a value CSS `text-overflow: ellipsis` is visually
   * clipping) — a screen reader already reads the complete text straight off
   * `children`, unaffected by the CSS truncation, so there is nothing extra
   * to describe. Skips `aria-describedby` and the always-present (`sr-only`
   * when hidden) description node, which would otherwise announce — and let
   * `getByText`-style lookups match — the same string twice.
   */
  visualOnly?: boolean;
}

interface Position {
  top: number;
  left: number;
}

/**
 * A focus- AND hover-driven tooltip (a11y §8.6.15) — replaces the mock's
 * `title`-only affordances, which are unreachable by keyboard. The trigger is
 * wired with `aria-describedby`; the tooltip appears on focus or hover.
 *
 * Positioned via `position: fixed` from the trigger's measured bounding box,
 * not CSS `position: absolute` off a relative-positioned ancestor — a trigger
 * living inside a truncating table cell (`overflow: hidden`, needed so free
 * text can't grow the cell and misalign the row) would otherwise clip the
 * popup along with it. `position: fixed` escapes that ancestor entirely.
 */
export function Tooltip({ content, children, visualOnly = false }: TooltipProps): React.JSX.Element {
  const id = useId();
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);

  const show = (): void => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect !== undefined) setPosition({ top: rect.top, left: rect.left + rect.width / 2 });
    setVisible(true);
  };
  const hide = (): void => {
    setVisible(false);
  };

  // A tooltip positioned from a one-time measurement shouldn't drift from its
  // trigger while the page scrolls underneath it — close it instead. `capture:
  // true` is required: scroll events don't bubble, including from a nested
  // scrollable table, so only the capture phase sees them at the window.
  useEffect(() => {
    if (!visible) return;
    window.addEventListener('scroll', hide, true);
    return () => {
      window.removeEventListener('scroll', hide, true);
    };
  }, [visible]);

  const popupStyle = position !== null ? { top: position.top, left: position.left } : undefined;

  return (
    <span
      ref={anchorRef}
      className="ds-tooltip-anchor"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {visualOnly ? children : cloneElement(children, { 'aria-describedby': id })}
      {visualOnly ? (
        visible ? (
          <span role="tooltip" aria-hidden="true" className="ds-tooltip" style={popupStyle}>
            {content}
          </span>
        ) : null
      ) : (
        <span
          role="tooltip"
          id={id}
          className={visible ? 'ds-tooltip' : 'sr-only'}
          style={visible ? popupStyle : undefined}
        >
          {content}
        </span>
      )}
    </span>
  );
}
